import { useState } from "react";
import { ApiError } from "@/lib/api.js";
import { useTenant } from "@/lib/tenant-context.js";
import { Button } from "@/components/ui/button.js";
import { Badge } from "@/components/ui/badge.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { Textarea } from "@/components/ui/textarea.js";
import { Separator } from "@/components/ui/separator.js";
import { useToast } from "@/components/ui/toast.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog.js";
import { BannerStack } from "@/views/banner-stack.js";
import { RecurrenceEditor } from "@/views/recurrence-editor.js";
import { buildUpdatePayload } from "@/lib/recurrence.js";
import { formatDate, formatTime } from "@/lib/format.js";
import type { Appointment, Customer, Resource, Service, SyncConflict } from "@/lib/types.js";

/**
 * Appointment detail + lifecycle actions per Flows I (reschedule) and J (cancel)
 * + REQ-039 (no-show / complete). Surfaced from calendar grid, dispatch, and the
 * day list. Provides:
 *   • Identity card with banners (REQ-035)
 *   • Cancel with reason
 *   • Mark no-show
 *   • Mark completed
 *   • Reschedule (time + optional resource swap)
 *   • Edit recurring instance (when series_id present) — opens RecurrenceEditor
 *     with from_instance_appointment_id pre-set, enabling all three scopes.
 */
export function AppointmentDialog({
  open,
  onOpenChange,
  appointment,
  customers,
  services,
  resources,
  conflicts,
  onChanged
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  appointment: Appointment | null;
  customers: Customer[];
  services: Service[];
  resources: Resource[];
  conflicts: SyncConflict[];
  onChanged: () => void;
}): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const [editingRecurrence, setEditingRecurrence] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);

  if (!appointment) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent />
      </Dialog>
    );
  }

  const customer = customers.find((c) => c.id === appointment.customer_id);
  const service = services.find((s) => s.id === appointment.service_id);
  const resource = resources.find((r) => r.id === appointment.primary_resource_id);
  const isTerminal = appointment.lifecycle_state === "completed"
    || appointment.lifecycle_state === "cancelled"
    || appointment.lifecycle_state === "no_show";

  // Local non-null binding so closures don't have to redo the narrowing.
  const appt = appointment;

  const withLifecycle = async (action: () => Promise<unknown>, label: string): Promise<void> => {
    try {
      await action();
      toast({ title: label, variant: "success" });
      onChanged();
      onOpenChange(false);
    } catch (error) {
      const code = error instanceof ApiError ? error.code : "error";
      const msg = error instanceof Error ? error.message : String(error);
      toast({ title: code, description: msg, variant: "destructive" });
    }
  };

  const reschedule = async (
    newStartsAt: string,
    newResourceId: string | undefined,
    newDurationMinutes: number
  ): Promise<void> => {
    const oldDuration = Math.round(
      (Date.parse(appt.ends_at) - Date.parse(appt.starts_at)) / 60_000
    );
    const startsMs = Date.parse(newStartsAt);
    return withLifecycle(async () => {
      await api.rescheduleAppointment(appt.id, {
        starts_at: newStartsAt,
        primary_resource_id: newResourceId ?? appt.primary_resource_id
      });
      // If the user also changed duration, commit the new ends_at via PATCH.
      // This is the same wire format calendar drag-resize uses.
      if (newDurationMinutes !== oldDuration) {
        const endsAt = new Date(startsMs + newDurationMinutes * 60_000).toISOString();
        await api.patchAppointment(appt.id, {
          ends_at: endsAt,
          idempotency_key: `${editJti()}:resize:${appt.id}:${Date.now()}`
        });
      }
    }, "Updated");
  };

  const cancelWith = async (reason: string): Promise<void> =>
    withLifecycle(() => api.cancelAppointment(appt.id, reason), "Cancelled");

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{service?.name ?? "Appointment"}</DialogTitle>
            <DialogDescription>
              {appointment.customer_name ?? customer?.name ?? "—"} · {formatDate(appointment.starts_at)} · {formatTime(appointment.starts_at)}–
              {formatTime(appointment.ends_at)}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 text-sm">
            <div className="flex flex-wrap gap-1.5">
              <Badge variant={isTerminal ? "outline" : "success"} className="capitalize">
                {appointment.lifecycle_state.replace("_", " ")}
              </Badge>
              <Badge variant="outline" className="capitalize">
                via {appointment.created_via}
              </Badge>
              {/*
                PRD principal-scoped-reads-and-crm-refs (REQ-PSR-008): prefer
                the appointment's snapshot, falling back to the live resource
                lookup for legacy rows that pre-date the snapshot column.
              */}
              {(appointment.resource_name ?? resource?.name) && (
                <Badge variant="outline">{appointment.resource_name ?? resource?.name}</Badge>
              )}
              {appointment.series_id && <Badge variant="outline">recurring</Badge>}
            </div>

            <BannerStack appointment={appointment} conflicts={conflicts} />

            {appointment.notes && (
              <div className="rounded-md border bg-muted/40 p-2 text-xs">{appointment.notes}</div>
            )}

            {!isTerminal && rescheduling && (
              <RescheduleBlock
                appointment={appointment}
                resources={resources}
                onCancel={() => setRescheduling(false)}
                onSubmit={async (newStartsAt, newResourceId, newDurationMinutes) => {
                  await reschedule(newStartsAt, newResourceId, newDurationMinutes);
                  setRescheduling(false);
                }}
              />
            )}
          </div>

          {!isTerminal && (
            <>
              <Separator />
              <DialogFooter className="flex flex-wrap gap-2 sm:justify-between">
                <div className="flex flex-wrap gap-1">
                  {appointment.lifecycle_state === "pending" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => withLifecycle(() => api.confirmAppointment(appointment.id), "Confirmed")}
                    >
                      Confirm
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRescheduling((v) => !v)}
                  >
                    {rescheduling ? "Hide" : "Edit time"}
                  </Button>
                  {appointment.series_id && (
                    <Button variant="outline" size="sm" onClick={() => setEditingRecurrence(true)}>
                      Edit recurring…
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => withLifecycle(() => api.completeAppointment(appointment.id), "Completed")}
                  >
                    Complete
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => withLifecycle(() => api.noShowAppointment(appointment.id), "Marked no-show")}
                  >
                    No-show
                  </Button>
                  <CancelButton onCancel={(reason) => void cancelWith(reason)} />
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {appointment.series_id && (
        <SeriesInstanceEditor
          appointment={appointment}
          open={editingRecurrence}
          onOpenChange={setEditingRecurrence}
          onSaved={() => {
            toast({ title: "Series updated", variant: "success" });
            onChanged();
            setEditingRecurrence(false);
            onOpenChange(false);
          }}
        />
      )}
    </>
  );
}

/**
 * Hosts the RecurrenceEditor in a tightly-typed wrapper so the parent dialog can
 * gate `appointment.series_id` truthiness once at the wrapper boundary instead of
 * sprinkling non-null assertions through the editor's onSubmit closure.
 */
function SeriesInstanceEditor({
  appointment,
  open,
  onOpenChange,
  onSaved
}: {
  appointment: Appointment;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}): React.JSX.Element {
  const { api } = useTenant();
  const seriesId = appointment.series_id;
  if (!seriesId) return <></>;
  return (
    <RecurrenceEditor
      open={open}
      onOpenChange={onOpenChange}
      isEdit
      fromInstanceAppointmentId={appointment.id}
      startsAt={appointment.starts_at}
      onSubmit={async ({ scope, rrule, fromInstanceAppointmentId }) => {
        const payload = buildUpdatePayload({
          scope: scope ?? "all",
          rrule: rrule || undefined,
          fromInstanceAppointmentId
        });
        await api.updateRecurrenceSeries(seriesId, payload);
        onSaved();
      }}
    />
  );
}

function CancelButton({ onCancel }: { onCancel: (reason: string) => void }): React.JSX.Element {
  const [armed, setArmed] = useState(false);
  const [reason, setReason] = useState("");
  if (!armed) {
    return (
      <Button variant="destructive" size="sm" onClick={() => setArmed(true)}>
        Cancel
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Input
        autoFocus
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Cancellation reason"
        className="w-48 h-8"
      />
      <Button
        variant="destructive"
        size="sm"
        onClick={() => onCancel(reason || "no_reason_given")}
        disabled={!reason}
      >
        Confirm
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setArmed(false)}>
        Back
      </Button>
    </div>
  );
}

function RescheduleBlock({
  appointment,
  resources,
  onCancel,
  onSubmit
}: {
  appointment: Appointment;
  resources: Resource[];
  onCancel: () => void;
  onSubmit: (newStartsAt: string, newResourceId: string | undefined, newDurationMinutes: number) => void | Promise<void>;
}): React.JSX.Element {
  const initialDuration = Math.max(
    5,
    Math.round((Date.parse(appointment.ends_at) - Date.parse(appointment.starts_at)) / 60_000)
  );
  const [whenLocal, setWhenLocal] = useState(toLocalDateTime(appointment.starts_at));
  const [resourceId, setResourceId] = useState(appointment.primary_resource_id);
  const [duration, setDuration] = useState(initialDuration);
  const [submitting, setSubmitting] = useState(false);

  return (
    <form
      className="flex flex-col gap-2 rounded-md border p-3"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!whenLocal) return;
        setSubmitting(true);
        try {
          await onSubmit(new Date(whenLocal).toISOString(), resourceId, Math.max(5, duration));
        } finally {
          setSubmitting(false);
        }
      }}
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <Label>New time</Label>
          <Input type="datetime-local" value={whenLocal} onChange={(e) => setWhenLocal(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <Label>Resource</Label>
          <select
            value={resourceId}
            onChange={(e) => setResourceId(e.target.value)}
            className="h-9 rounded-md border bg-background px-2 text-sm"
          >
            {resources.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <Label>Duration (minutes)</Label>
          <Input
            type="number"
            min={5}
            max={720}
            step={5}
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value) || initialDuration)}
          />
        </div>
      </div>
      {appointment.notes && (
        <Textarea value={appointment.notes} readOnly className="text-xs" />
      )}
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={submitting}>
          {submitting ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}

function editJti(): string {
  return globalThis.crypto?.randomUUID?.() ?? `jti-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function toLocalDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
